import React, { useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";

import { cuesApi } from "../../imports";
import useGetMediaLocation from "../hooks/useGetMediaLocation";

export default function Audio({ cue, cloud, forceMute = false }) {
	const { projectId } = useParams();
	const audioRef = useRef();
	const mediaLocation = useGetMediaLocation();

	const audioSource = useMemo(() => {
		// This ensures the audio can load when switching between sources on the same cue
		audioRef?.current?.load();
		return <source src={`${mediaLocation}${cue.filename}`} type="audio/mp3" />;
	}, [cue.filename, mediaLocation]);

	const fadeOutAudio = () => {
		if (!audioRef.current) return;

		const audio = audioRef?.current;
		const fadeDuration = 1000; // Fade duration in milliseconds
		const fadeStep = 0.01; // Decrease the volume by this amount on each step
		const fadeInterval = fadeDuration * fadeStep; // Calculate the time between each step

		const fade = () => {
			if (!audio) return;

			// Lower the volume
			audio.volume = Math.max(audio.volume - fadeStep, 0);

			// If the volume is still greater than 0, continue fading
			if (audio.volume > 0) {
				setTimeout(fade, fadeInterval);
			} else {
				audio.pause();
			}
		};

		fade();
	};

	useEffect(() => {
		if (cue.status === "coming") {
			const audio = audioRef?.current;
			audio.volume = (cue.volume || 100) / 100;
			audioRef?.current?.play();
		}
		if (cue.status === "going") fadeOutAudio();
	}, [cue.status, cue?.volume]);

	useEffect(() => {
		const audio = audioRef?.current || null;
		audio.volume = (cue.volume || 100) / 100;
	}, [cue.volume]);

	return (
		<div className="h-0 opacity-0">
			Audio
			<audio
				id={`${cue.id}_${cue.filename}`}
				ref={audioRef}
				controls={true}
				autoPlay={false}
				muted={forceMute}
				playsInline={true}
				onCanPlay={(e) => {
					if (!["onair", "coming"].includes(cue.status)) {
						// console.log("PAUSED");
						e.target.pause();
					}
					// e.target.play();
					cuesApi.updateCue(cue.id, projectId, {
						length: e.target.duration,
					});
				}}
				onTimeUpdate={(e) => {
					if (cue?.loop && e?.target?.currentTime >= cue?.length - 0.1) {
						e.target.currentTime = 0;
						e.target.play();
						cuesApi.updateCue(cue.id, projectId, {
							currentTime: 0,
						});
					}
					if (e.target.currentTime > cue.currentTime + 0.5) {
						cuesApi.updateCue(cue.id, projectId, {
							currentTime: e.target.currentTime,
						});
					}
				}}
				onPlay={(e) => {
					if (e.target.currentTime > 0) {
						e.target.currentTime = 0;
					}
				}}
				onEnded={(e) => {
					// console.log("on ended");
					if (!cue.loop) {
						cuesApi.updateCue(cue.id, projectId, {
							status: "going",
						});
						setTimeout(() => {
							cuesApi.updateCue(cue.id, projectId, {
								currentTime: 0,
							});
							e.target.currentTime = 0;
						}, 1000);
					}
				}}
			>
				{audioSource}
			</audio>
		</div>
	);
}
